import React, { useMemo, useState } from 'react';
import { keyBy, noop } from 'lodash';
import {
    ChoiceGroup,
    ChoiceGroupItem,
    Form,
    FormControl,
    FormElementController,
    useForm,
    z,
    zodResolver,
} from '@fiverr-private/forms';
import { Button } from '@fiverr-private/button';
import { I18n } from '@fiverr-private/i18n-react';
import { useModalContext } from '@fiverr-private/overlay';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Alert, AlertText } from '@fiverr-private/feedback';
import { SellerEntityTypes } from '@fiverr-private/upmarket_common';
import { logger } from '@fiverr-private/obs';
import { getCurrencySymbol } from '@fiverr-private/localization';
import { SupportLink } from '../../../DocumentLinks';
import { useTemplatesData } from '../../../../hooks';
import { LegalTermsDisclaimer } from '../../../LegalTermsDisclaimer/LegalTermsDisclaimer';
import { formatPrice } from '../../../../utils/currency';
import { reportEventsApi } from '../../../../biEvents';
import { GetSellerTemplatesDataResponse, SentTemplateData } from '../../../../api';
import { SendModalStepProps } from './types';

const TRANSLATION_PREFIX = 'legal_documents_package.send_modal.selection_step';

export const TemplateSelectionStep = ({
    onSubmit = noop,
    sellerId,
    configurations,
    biData,
    onSend,
    handlePayment,
    sellerName,
    sellerEntityType,
}: SendModalStepProps) => {
    const { templatesQuery, sellerDataQuery } = useTemplatesData({ sellerId });
    const { close } = useModalContext();
    const [isSending, setIsSending] = useState(false);

    const displayedTemplates = useMemo(() => {
        const documentsByTemplateId = keyBy(sellerDataQuery?.data || [], 'templateId');
        return templatesQuery?.data?.filter((data) => !documentsByTemplateId[data._id]) || [];
    }, [sellerDataQuery?.data, templatesQuery?.data]);

    const schema = z.object({
        documents: z.array(z.string()).nonempty(),
    });

    const methods = useForm({
        resolver: zodResolver(schema),
    });

    methods.watch();

    const waitForUpdatedData = async (
        currentData?: GetSellerTemplatesDataResponse,
        onSuccess?: () => void,
        maxRetries = 3,
        baseDelay = 100
    ) => {
        let retriesLeft = maxRetries;
        let delay = baseDelay;
        let updatedData;

        function timeout(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        }

        let oldIds = new Set(currentData?.map((template) => template.templateId));

        const hasNewId = (newIds: string[]) => newIds.some((templateId) => !oldIds.has(templateId));

        while (retriesLeft > 0) {
            await timeout(delay);
            updatedData = await sellerDataQuery.refetch();

            const newIds = updatedData?.data?.map((template: SentTemplateData) => template.templateId) ?? [];

            // execute success and break if data changed
            if (hasNewId(newIds)) {
                onSuccess?.();
                return updatedData;
            }

            oldIds = new Set(newIds);
            retriesLeft--;
            delay *= 2;
        }

        return updatedData;
    };

    const handleSubmit = async () => {
        try {
            reportEventsApi.clickOnSelectDocuments(biData);
            setIsSending(true);

            // Both for paying and discounted clients - KAFKA
            const { isRedirecting } = await handlePayment(methods?.getValues()?.documents);
            if (isRedirecting) return;

            await waitForUpdatedData(sellerDataQuery?.data, onSend);
            onSubmit();
            setIsSending(false);
        } catch (error) {
            logger.error(error as Error);
            setIsSending(false);
        }
    };

    const { isFree, price, currency = 'USD' } = configurations?.legalDocumentsPackageData || {};

    const selectedTemplates = methods?.getValues()?.documents;
    const displayedPrice =
        !isFree && selectedTemplates?.length
            ? `${formatPrice(price * methods?.getValues()?.documents?.length)}${getCurrencySymbol({
                  currencyCode: currency,
              })}`
            : undefined;
    const isTeamAccount = sellerEntityType === SellerEntityTypes.TEAM;

    return (
        <Stack direction="column" maxHeight="100%" overflow="auto">
            <Text paddingBottom={isTeamAccount ? '2' : '5'}>
                <I18n
                    k={`${TRANSLATION_PREFIX}.description`}
                    templates={{
                        link: (text) => <SupportLink text={text as string} />,
                    }}
                />
            </Text>
            {isTeamAccount && (
                <Stack marginBottom="5">
                    <Alert variant="info" showIcon={false}>
                        <AlertText>
                            <I18n
                                k={`${TRANSLATION_PREFIX}.teamInfo`}
                                params={{
                                    sellerName,
                                }}
                            />
                        </AlertText>
                    </Alert>
                </Stack>
            )}
            <Text>
                <I18n k={`${TRANSLATION_PREFIX}.content_title`} />
            </Text>
            <Form methods={methods} onSubmit={handleSubmit}>
                <FormControl name="documents">
                    <FormElementController
                        render={({ field: { onChange, value = [], ref } }) => (
                            <ChoiceGroup
                                direction="column"
                                multiple
                                value={value}
                                onChange={(event) => onChange(event)}
                                ref={ref}
                            >
                                {displayedTemplates?.map((template) => (
                                    <ChoiceGroupItem
                                        value={template._id}
                                        key={template._id}
                                        checkboxPositioning="center"
                                    >
                                        {template.fileName}
                                    </ChoiceGroupItem>
                                ))}
                            </ChoiceGroup>
                        )}
                    />
                </FormControl>

                <Container position="sticky" bottom="0px" backgroundColor="white">
                    <LegalTermsDisclaimer paddingTop="5" />

                    <Stack justifyContent="flexEnd" paddingTop="8">
                        <Button variant="ghost" type="button" onClick={close}>
                            <I18n k={`${TRANSLATION_PREFIX}.cancel`} />
                        </Button>
                        <Button
                            disabled={!selectedTemplates?.length || isSending}
                            loading={isSending}
                            onClick={handleSubmit}
                        >
                            {!isFree ? (
                                <Stack gap="1">
                                    <I18n k={`${TRANSLATION_PREFIX}.submit_essential_package`} />
                                    <Text color="white" fontWeight="semibold">
                                        {!!selectedTemplates?.length && `[${displayedPrice}]`}
                                    </Text>
                                </Stack>
                            ) : (
                                <I18n k={`${TRANSLATION_PREFIX}.submit`} />
                            )}
                        </Button>
                    </Stack>
                </Container>
            </Form>
        </Stack>
    );
};
